import React from "react";
import {
    Calendar as CalendarIcon,
    MapPin,
    FileText,
    User,
    Phone,
    Headphones,
    Hash,
    X,
    ExternalLink,
} from "lucide-react";
import dayjs from "dayjs";
import { AppointmentItem } from "../api/Appointment";
import { getAppointmentDetailPath } from "../utils/appointmentRoute";

/**
 * Hộp thoại chi tiết lịch hẹn khi hover — dùng chung cho bảng lịch (trang Lịch hẹn)
 * và bảng kéo thả (Lịch tư vấn / Lịch thực hiện) để hai nơi luôn hiển thị cùng một
 * bộ thông tin.
 */

export const FALLBACK_TAG_COLOR = "#64748b";

export const hexToRgba = (hex: string, alpha: number) => {
    let h = (hex || "").replace("#", "").trim();
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    if (!/^[0-9a-fA-F]{6}$/.test(h)) h = FALLBACK_TAG_COLOR.replace("#", "");
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

export interface AppointmentDetailInfo {
    id: number;
    code: string;
    name: string;
    phone: string;
    service: string;
    /** DD/MM/YYYY */
    dateLabel: string;
    /** HH:mm */
    startLabel: string;
    telesale: string;
    pageStaff: string;
    branch: string;
    note: string;
    creator: string;
    tagName: string;
    tagColor: string;
    detailPath: string | null;
}

const staffName = (s: any) => s?.name || s?.fullname || "";

/** Rút thông tin hiển thị từ một lịch hẹn của API */
export const buildAppointmentDetailInfo = (a: AppointmentItem): AppointmentDetailInfo => {
    const start = a?.scheduled_at ? dayjs(String(a.scheduled_at).replace(" ", "T")) : null;
    const tag = a.customer_tag || null;
    const services = Array.isArray(a.services) && a.services.length
        ? a.services
        : (a.service ? [a.service] : []);

    return {
        id: a.id,
        code: a.code || "",
        name: a.customer_name
            || a.channel_customer?.display_name
            || a.customer?.display_name
            || a.customer?.name
            || "Khách chưa có tên",
        phone: a.customer_phone || a.channel_customer?.phone || "",
        service: services.map((s: any) => s?.name).filter(Boolean).join(", "),
        dateLabel: start?.isValid() ? start.format("DD/MM/YYYY") : "",
        startLabel: start?.isValid() ? start.format("HH:mm") : "",
        telesale: staffName(a.telesale),
        pageStaff: staffName(a.staff),
        branch: a.branch?.name || "",
        note: a.note || a.notes?.[0]?.content || "",
        creator: a.creator?.fullname || "",
        tagName: tag?.name || a.customer_tag_status_text || a.status_text || "",
        tagColor: tag?.color || FALLBACK_TAG_COLOR,
        detailPath: getAppointmentDetailPath(a.customer_tag_page ?? tag?.page, a.id),
    };
};

/** Vị trí hộp thoại bám theo phần tử được hover, luôn nằm trong màn hình */
export const computePopoverPosition = (rect: DOMRect): React.CSSProperties => {
    const width = window.innerWidth < 370 ? window.innerWidth - 20 : 350;

    let left = rect.right + 10;
    let originX = "left";

    if (window.innerWidth < 640) {
        left = (window.innerWidth - width) / 2;
        originX = "center";
    } else if (left + width > window.innerWidth - 10) {
        left = rect.left - width - 10;
        originX = "right";
    }
    const maxLeft = window.innerWidth - width - 10;
    if (left > maxLeft) left = maxLeft;
    if (left < 10) left = 10;

    const styles: React.CSSProperties = { left: `${left}px` };
    let originY = "top";

    if (rect.top > window.innerHeight / 2) {
        let bottomDist = window.innerHeight - rect.bottom - 20;
        if (bottomDist < 10) bottomDist = 10;
        styles.bottom = `${bottomDist}px`;
        originY = "bottom";
    } else {
        let topDist = rect.top - 20;
        if (topDist < 10) topDist = 10;
        styles.top = `${topDist}px`;
    }

    return { ...styles, transformOrigin: `${originX} ${originY}` };
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="grid grid-cols-7 gap-2 border-b border-gray-100 dark:border-gray-700 pb-2 last:border-b-0 last:pb-0">
        <div className="col-span-2 text-xs text-gray-500 dark:text-gray-400 font-medium">{label}</div>
        <div className="col-span-5 min-w-0">{children}</div>
    </div>
);

interface Props {
    info: AppointmentDetailInfo | null;
    isVisible: boolean;
    positionStyles: React.CSSProperties;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
    onClose: () => void;
    onOpenDetail: (info: AppointmentDetailInfo) => void;
}

const AppointmentDetailPopover = ({
    info,
    isVisible,
    positionStyles,
    onMouseEnter,
    onMouseLeave,
    onClose,
    onOpenDetail,
}: Props) => {
    if (!info || !isVisible) return null;

    return (
        <div
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={(e) => e.stopPropagation()}
            className="fixed z-[60] bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-[350px] max-w-[calc(100vw-20px)] flex flex-col overflow-hidden transition-all duration-200"
            style={{ ...positionStyles, opacity: 1, maxHeight: "calc(100vh - 20px)" }}
        >
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center gap-2 bg-gray-50 dark:bg-gray-900 shrink-0">
                <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 whitespace-nowrap">Chi tiết lịch hẹn</h3>
                <div className="flex items-center gap-2 min-w-0">
                    {info.tagName && (
                        <div
                            className="px-2 py-0.5 rounded text-[11px] font-semibold truncate max-w-[170px]"
                            style={{ backgroundColor: hexToRgba(info.tagColor, 0.14), color: info.tagColor }}
                            title={info.tagName}
                        >
                            {info.tagName}
                        </div>
                    )}
                    <button onClick={onClose} className="md:hidden text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            <div className="p-4 space-y-3 dark:text-gray-300 overflow-y-auto max-h-[260px] sm:max-h-[360px] custom-scrollbar overscroll-contain">
                <Row label="Khách hàng:">
                    <div className="text-sm text-gray-900 dark:text-white font-bold break-words">{info.name}</div>
                </Row>
                {info.phone && (
                    <Row label="Điện thoại:">
                        <a href={`tel:${info.phone}`} className="text-xs flex items-center text-gray-900 dark:text-gray-200 hover:text-blue-600">
                            <Phone className="w-3 h-3 mr-1 text-gray-400 shrink-0" /> {info.phone}
                        </a>
                    </Row>
                )}
                <Row label="Dịch vụ:">
                    <div className="text-sm text-blue-600 dark:text-blue-400 font-semibold break-words">
                        {info.service || "Chưa chọn dịch vụ"}
                    </div>
                </Row>
                <Row label="Thời gian:">
                    <div className="text-xs flex items-center text-gray-900 dark:text-gray-200">
                        <CalendarIcon className="w-3 h-3 mr-1 text-gray-400 shrink-0" />
                        {info.dateLabel
                            ? <>Ngày {info.dateLabel}, <span className="font-semibold ml-1">{info.startLabel}</span></>
                            : "Chưa đặt lịch"}
                    </div>
                </Row>
                <Row label="NV Telesale:">
                    <div className="text-xs flex items-center text-gray-900 dark:text-gray-200">
                        <Headphones className="w-3 h-3 mr-1 text-gray-400 shrink-0" /> {info.telesale || "Chưa phân công"}
                    </div>
                </Row>
                <Row label="NV trực page:">
                    <div className="text-xs flex items-center text-gray-900 dark:text-gray-200">
                        <User className="w-3 h-3 mr-1 text-gray-400 shrink-0" /> {info.pageStaff || "Chưa phân công"}
                    </div>
                </Row>
                <Row label="Chi nhánh:">
                    <div className="text-xs flex items-center text-gray-900 dark:text-gray-200 font-medium">
                        <MapPin className="w-3 h-3 mr-1 text-red-400 shrink-0" /> {info.branch || "Chưa chọn chi nhánh"}
                    </div>
                </Row>
                <Row label="Ghi chú:">
                    <div className="text-xs text-gray-900 dark:text-gray-300 italic whitespace-pre-line line-clamp-4">
                        {info.note || "Không có ghi chú"}
                    </div>
                </Row>
                <Row label="Mã lịch:">
                    <div className="text-xs flex items-center text-gray-900 dark:text-gray-200 font-medium">
                        <Hash className="w-3 h-3 mr-1 text-gray-400 shrink-0" /> {info.code || `#${info.id}`}
                    </div>
                </Row>
                <Row label="Người tạo:">
                    <div className="text-xs flex items-center text-gray-900 dark:text-gray-200">
                        <FileText className="w-3 h-3 mr-1 text-blue-400 shrink-0" /> {info.creator || "Không rõ"}
                    </div>
                </Row>
            </div>

            <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex justify-end shrink-0">
                <button
                    type="button"
                    onClick={() => onOpenDetail(info)}
                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline flex items-center transition-colors"
                >
                    Xem trang chi tiết <ExternalLink className="w-3.5 h-3.5 ml-1" />
                </button>
            </div>
        </div>
    );
};

export default AppointmentDetailPopover;
